import { DateTime } from 'luxon';

/**
 * Time utility functions for handling timezone conversions
 */

/**
 * Get the user's timezone from localStorage or system defaults
 * @returns {string} Timezone string (e.g., 'Europe/London')
 */
export const getUserTimezone = () => {
  return localStorage.getItem('timezone') || 
         Intl.DateTimeFormat().resolvedOptions().timeZone || 
         'UTC';
};

/**
 * Convert a UTC time string to local time
 * @param {string} utcTime - Time string in HH:MM:SS format (UTC)
 * @returns {string} Time string in HH:MM:SS format (local timezone)
 */
export const convertUTCToLocalTime = (utcTime) => {
  if (!utcTime) return utcTime;
  
  console.log(`🔄 Converting UTC time ${utcTime} to local time`);
  
  try {
    // Handle various time formats
    const timeParts = utcTime.split(':');
    if (timeParts.length < 2) {
      console.error('Invalid time format:', utcTime);
      return utcTime;
    }
    
    // Create a UTC DateTime with today's date and the given time
    const utcDateTime = DateTime.utc().set({
      hour: parseInt(timeParts[0], 10),
      minute: parseInt(timeParts[1], 10),
      second: timeParts.length > 2 ? parseInt(timeParts[2], 10) : 0,
    });
    
    // Convert to the user's timezone
    const timezone = getUserTimezone();
    const localDateTime = utcDateTime.setZone(timezone);
    
    // Format as HH:MM:SS
    const localTime = localDateTime.toFormat('HH:mm:ss');
    
    console.log(`🕒 Converted UTC time ${utcTime} to local time ${localTime} (${timezone})`);
    return localTime;
  } catch (error) {
    console.error('Error converting UTC to local time:', error);
    return utcTime;
  }
};

/**
 * Convert a local time string to UTC
 * @param {string} localTime - Time string in HH:MM:SS format (local timezone)
 * @returns {string} Time string in HH:MM:SS format (UTC)
 */
export const convertLocalTimeToUTC = (localTime) => {
  if (!localTime) return localTime;
  
  console.log(`🔄 Converting local time ${localTime} to UTC`);
  
  try {
    // Handle various time formats
    const timeParts = localTime.split(':');
    if (timeParts.length < 2) {
      console.error('Invalid time format:', localTime);
      return localTime;
    }

    // Create a DateTime in the user's timezone with today's date and the given time
    const timezone = getUserTimezone();
    const localDateTime = DateTime.now().setZone(timezone).set({
      hour: parseInt(timeParts[0], 10),
      minute: parseInt(timeParts[1], 10),
      second: timeParts.length > 2 ? parseInt(timeParts[2], 10) : 0,
    });
    
    // Convert to UTC
    const utcDateTime = localDateTime.toUTC();
    
    // Format as HH:MM:SS
    const utcTime = utcDateTime.toFormat('HH:mm:ss');
    
    console.log(`🕒 Converted local time ${localTime} to UTC time ${utcTime} (${timezone})`);
    return utcTime;
  } catch (error) {
    console.error('Error converting local to UTC time:', error);
    return localTime;
  }
};

/**
 * Format a time string for display
 * @param {string|object} time - Time string in HH:MM:SS format or TimeValue object
 * @param {boolean} convertFromUTC - Whether to convert from UTC to local time
 * @returns {string} Formatted time string (e.g., "3:00 PM")
 */
export const formatTimeForDisplay = (time, convertFromUTC = false) => {
  if (!time) return '';
  
  try {
    console.log('formatTimeForDisplay received:', time, 'type:', typeof time);
    
    // Handle TimeValue objects or any object with a value property
    let timeString = '';
    
    if (typeof time === 'object' && time !== null) {
      console.log('Handling object type in formatTimeForDisplay');
      
      // For TimeValue or TimeValue-like objects with value property
      if (time.value !== undefined && typeof time.value === 'string') {
        console.log('Using .value property:', time.value);
        timeString = time.value;
      } 
      // Try toString method
      else if (time.toString && typeof time.toString === 'function') {
        try {
          const strValue = time.toString();
          if (strValue && typeof strValue === 'string' && strValue.includes(':')) {
            console.log('Using toString() result:', strValue);
            timeString = strValue;
          } else {
            console.log('toString() did not return a valid time string:', strValue);
          }
        } catch (err) {
          console.error('Error in toString():', err);
        }
      }
      
      // If we couldn't extract a time string, return empty
      if (!timeString) {
        console.error('Could not extract time string from object:', time);
        return '';
      }
    } else if (typeof time === 'string') {
      // Use the string directly
      timeString = time;
    } else {
      // For other types, try to convert to string
      try {
        timeString = String(time);
        console.log('Converted to string:', timeString);
      } catch (err) {
        console.error('Could not convert to string:', err);
        return '';
      }
    }
    
    // Ensure timeString has the expected format (contains ":")
    if (!timeString.includes(':')) {
      console.error('Invalid time format (no colons):', timeString);
      return timeString; // Return original string if it doesn't look like a time
    }
    
    // Convert from UTC if needed
    const timeToFormat = convertFromUTC ? convertUTCToLocalTime(timeString) : timeString;
    
    // Parse the time
    const timeParts = timeToFormat.split(':');
    if (timeParts.length < 2) {
      console.error('Invalid time format (not enough parts):', timeToFormat);
      return timeString;
    }
    
    // Ensure hour and minute are valid numbers
    const hour = parseInt(timeParts[0], 10);
    const minute = parseInt(timeParts[1], 10);
    
    if (isNaN(hour) || isNaN(minute)) {
      console.error('Invalid hour or minute:', hour, minute);
      return timeString;
    }
    
    // Format with AM/PM
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    const formattedTime = `${hour12}:${minute.toString().padStart(2, '0')} ${period}`;
    
    return formattedTime;
  } catch (error) {
    console.error('Error formatting time for display:', error);
    return typeof time === 'string' ? time : '';
  }
}; 